import { targetingService } from './';
import { logger } from '../utils';
import { GptProvider } from '../providers';
import { context } from '../services';

type TaxonomyKeysConfiguration = {
	from: string;
	addConfidence?: 'site' | 'page' | 'all'; // default value prevents confidence from being added at all
	limit?: 'one-of-each'; // defaults to no limit; limit of `one-of-each` means one value will be taken from the 'site' AND 'page' keys
};

const TAXONOMY_NEW_KEYS_CONFIGURATION: Record<string, TaxonomyKeysConfiguration> = {
	main_vertical: {
		from: 'main_vertical',
		limit: 'one-of-each',
	},
	main_entity_type: {
		from: 'main_entity_type',
		limit: 'one-of-each',
	},
	genre: {
		from: 'genres',
		addConfidence: 'site',
	},
	subgenre: {
		from: 'subgenres',
		addConfidence: 'site',
	},
	vertical: {
		from: 'verticals',
		addConfidence: 'all',
	},
	themes: {
		from: 'themes',
		addConfidence: 'site',
	},
};

type TargetingKeyValuesFromTaxonomy = Partial<Record<string, string[]>>;

const TAXONOMY_TAGS_CONFIGURATION: Record<string, TaxonomyKeysConfiguration> = {
	fran: {
		from: 'franchises',
	},
};

type FandomTaxonomyCategoryKey = 'site' | 'page'; // key of WindowFandomTaxonomy

/**
 * NOTE: This currently refers to the IAB Content Taxonomy Mapping from version 2.2
 * IAB Content Taxonomy Mapping 2.2 -
 * https://github.com/InteractiveAdvertisingBureau/Taxonomies/blob/main/Content%20Taxonomies/Content%20Taxonomy%202.2.tsv
 * 	- This mapping can be targeted by OpenRTB 2.6 spec with the "cattax" field.
 * - Refer to section 3.2.13 Object: Site in the OpenRTB 2.6 spec for more information.
 * - OpenRTB 2.6 documentation - https://iabtechlab.com/wp-content/uploads/2022/04/OpenRTB-2-6_FINAL.pdf
 * - The structure of the strings will look like this: ['23', '13', ...] where the 23 and 13
 * are values that will be pulled out from the fandomTaxonomy object.
 */
type Ortb2IabCategorySiteFields = {
	cattax: number;
	cat?: string[];
	pagecat?: string[];
};

export const MAX_VALUE_LENGTH = 40;
export const MAX_MUNBER_OF_VALUES = 50;

const LOG_GROUP = 'FandomTaxonomyService';
const IAS_SAFETY_IDS: string[] = [
	'v9i3On', // Sensitive Topics
	'Rm3SiT', // Adult & Explicit Sexual Content
	'avbNf2', // Arms & Ammunition
	'XtODT3', // Crime & Harmful acts to individuals and Society and Human Right Violations
	'I4GWl6', // Death, Injury, or Military Conflict
	'mm3UXx', // Online piracy
	'HxqYV1', // Hate speech & acts of aggression
	'j9PaO9', // Obscenity and Profanity	Sensitive Topics
	'pg0WhF', // Illegal Drugs/Tobacco/eCigarettes/ Vaping/Alcohol
	'6i4dB6', // Spam or Harmful Content
	'8FD8nI', // Terrorism
	'Z7rJBM', // Sensitive Social Issues
	'I4GWl7', // Not in IAB taxonomy 2.2
	'MRkz4Q', // Brand Suitability and Risk
	'bsr001', // Floor
	'bsr002', // High Risk
	'bsr003', // Medium Risk
	'bsr004', // Low Risk
	'381', // News and Politics > Disasters
	'181', // Events and Attractions > Casinos & Gambling
	'524', // Sports > Poker and Professional Gambling
];

// This function is used to filter out empty values from the object
function filterValues<T>(o: T): T {
	if (typeof o !== 'object' || o === null) {
		return o;
	}

	return Object.keys(o).reduce((acc, key) => {
		if (o[key] && o[key].length > 0) {
			acc[key] = o[key];
		}
		return acc;
	}, {} as T);
}

function normalizeConfidence(confidence: number): string {
	// check if confidence is a number
	if (isNaN(confidence)) {
		return '';
	}
	if (confidence < 0.1) {
		return '_c0';
	}
	if (confidence < 0.2) {
		return '_c1';
	}
	if (confidence < 0.3) {
		return '_c2';
	}
	if (confidence < 0.4) {
		return '_c3';
	}
	if (confidence < 0.5) {
		return '_c4';
	}
	if (confidence < 0.6) {
		return '_c5';
	}
	if (confidence < 0.7) {
		return '_c6';
	}
	if (confidence < 0.8) {
		return '_c7';
	}
	if (confidence < 0.9) {
		return '_c8';
	}
	return '_c9';
}

export class FandomTaxonomy {
	categories: { [Key in FandomTaxonomyCategoryKey]?: string[] } = {};
	externalSafetyIds: string[] | undefined;
	externalMappedIds: string[] | undefined;

	constructor(private fandomTaxonomy = window.fandomTaxonomy) {}

	public static applyToTargetingService() {
		const fandomTaxonomy = new FandomTaxonomy();

		// Regular Key Values are just added to the targeting service
		targetingService.extend({
			...targetingService.dump(),
			...fandomTaxonomy.getKeyValues(),
			// Send the IAB categories from FandomTaxonomy as KVPs to GAM,
			// so that they can be passed downstream through AdX to our Ad partners (TTD etc)
			// For the time being. these KVPs should be mapped in the same way as our DTCO data is mapped in GAM.
			...fandomTaxonomy.getOrtb2IabCategorySiteFields(),
		});

		// Tags are added to the targeting service as well, but should be merged with the existing values (if possible)
		const tags = fandomTaxonomy.getTags();
		Object.keys(tags).forEach((key) => {
			const existing = targetingService.get(key) || [];
			const newValues = tags[key] || [];
			if (Array.isArray(existing)) {
				const merged = [...existing, ...newValues].filter(Boolean);
				targetingService.set(key, merged);
			} else {
				targetingService.set(key, [existing, newValues]);
			}
		});

		GptProvider.setConfig({
			pps: {
				taxonomies: {
					IAB_CONTENT_2_2: {
						values: [
							...new Set([
								...fandomTaxonomy.getIABCategories('site'),
								...fandomTaxonomy.getIABCategories('page'),
							]),
						],
					},
				},
			},
		});
	}

	get isPresent(): boolean {
		return !!this.fandomTaxonomy;
	}

	/**
	 * @param categoryKey - The page or site field in the fandomTaxonomy object
	 * @returns - The IAB categories for the specified category in the fandomTaxonomy object,
	 * 	appended with the UGC category tag if the iab_categories field is set
	 *
	 * Extracts the iab_categories field from the taxonomy object based on the passed in category param.
	 * Categories include 'site' and 'page', which can both be set on the fandomTaxonomy object.
	 * Additionally, this function appends the UGC category tag, if iab_categories field has any values set
	 * in the fandomTaxonomy object, on the specified category
	 * */
	getIABCategories(categoryKey: FandomTaxonomyCategoryKey): string[] {
		if (this.categories[categoryKey]) {
			return this.categories[categoryKey];
		}

		const helixValues = [...(this.fandomTaxonomy?.[categoryKey]?.iab_categories ?? [])];

		if (!this.externalSafetyIds && !this.externalMappedIds) {
			if (context.get('options.iasPageTagging')) {
				({ iasSafetyIds: this.externalSafetyIds, iasMappedIds: this.externalMappedIds } =
					FandomTaxonomy.getIasCategories());
			} else if (context.get('experiments.peer39')) {
				this.externalSafetyIds = context.get('peer39.safetyIds') || [];
				this.externalMappedIds = context.get('peer39.mappedIds') || [];
			}
		}

		// Always send the unmoderated UGC category (1217)
		let finalValues = ['1217'];

		// The BSR default category "Low Risk" (bsr004) should be used as default safety category
		if (this.externalSafetyIds && this.externalSafetyIds.length > 0) {
			finalValues = [...finalValues, ...this.externalSafetyIds];
		} else {
			finalValues.push('bsr004');
		}

		// The "Entertainment Content" (1014) category should be used as default content category
		if (helixValues.length > 0) {
			finalValues = [...finalValues, ...helixValues];
		} else if (this.externalMappedIds && this.externalMappedIds.length > 0) {
			finalValues = [...finalValues, ...this.externalMappedIds];
		} else {
			finalValues.push('1014');
		}

		this.categories[categoryKey] = finalValues;

		return finalValues;
	}

	/**
	 * Creates an object with optional fields of 'cat' and 'pagecat' that contain IAB Categories.
	 * The key/values match the OpenRTB2 spec for the site object
	 * 3.2.13 Object: Site - (https://www.iab.com/wp-content/uploads/2016/03/OpenRTB-API-Specification-Version-2-5-FINAL.pdf)
	 *
	 * These can both be used as Key Value Pairs (KVPs) for sending to GAM on Ad Requests.
	 * There was an agreement to use the same key names as in OpenRTB v2.5 or lower as the GAM Key Value pairs,
	 * so that its easier to switch to OpenRTB v2.6 in the future, and have matching key names that are documented in the OpenRTB spec.
	 *  */
	getOrtb2IabCategorySiteFields(): Ortb2IabCategorySiteFields {
		// version of the categories
		const cattax = 6; // 6 = Taxonomy 2.2

		const iabSiteCat = this.getIABCategories('site');
		const iabPageCat = this.getIABCategories('page');

		logger(LOG_GROUP, 'iabSiteCat: ', iabSiteCat, ' iabPageCat: ', iabPageCat);

		return {
			cattax,
			cat: iabSiteCat,
			pagecat: iabPageCat,
		};
	}

	getKeyValues(): TargetingKeyValuesFromTaxonomy {
		const kv: TargetingKeyValuesFromTaxonomy = Object.keys(TAXONOMY_NEW_KEYS_CONFIGURATION).reduce(
			(acc, key) => {
				acc[key] = this.getValueForKey(TAXONOMY_NEW_KEYS_CONFIGURATION[key]);
				return acc;
			},
			{} as TargetingKeyValuesFromTaxonomy,
		);

		return filterValues(kv);
	}

	getTags(): TargetingKeyValuesFromTaxonomy {
		const tags: TargetingKeyValuesFromTaxonomy = Object.keys(TAXONOMY_TAGS_CONFIGURATION).reduce(
			(acc, key) => {
				acc[key] = this.getValueForKey(TAXONOMY_TAGS_CONFIGURATION[key]);
				return acc;
			},
			{} as TargetingKeyValuesFromTaxonomy,
		);

		return filterValues(tags);
	}

	private getValueForKey(config: TaxonomyKeysConfiguration): string[] {
		const { from, addConfidence = false, limit } = config;

		// fetch the data from the taxonomy object and prefix values with 's_' or 'p_'
		let siteValues = this.getValueFromTaxonomyWithPrefix('site', from);
		let pageValues = this.getValueFromTaxonomyWithPrefix('page', from);

		// add confidence to the values if needed
		if (addConfidence === 'site' || addConfidence === 'all') {
			siteValues = siteValues.map(FandomTaxonomy.addConfidenceToValue);
		}
		if (addConfidence === 'page' || addConfidence === 'all') {
			pageValues = pageValues.map(FandomTaxonomy.addConfidenceToValue);
		}

		let combinedValues: FandomTaxonomyValue[] = [];

		// limit the values if needed in this special case
		if (limit === 'one-of-each') {
			if (siteValues.length) {
				combinedValues.push(siteValues[0]);
			}
			if (pageValues.length) {
				combinedValues.push(pageValues[0]);
			}
		} else {
			combinedValues = [...siteValues, ...pageValues];
		}

		combinedValues = FandomTaxonomy.orderByConfidence(combinedValues);

		// return string values
		const values = combinedValues.filter(Boolean).map(FandomTaxonomy.getNormalizedValue);

		// limit the number of values
		return values.slice(0, MAX_MUNBER_OF_VALUES);
	}

	private getValueFromTaxonomyWithPrefix(
		category: FandomTaxonomyCategoryKey,
		key: string,
	): FandomTaxonomyValue[] {
		const FANDOM_TAXONOMY_CATEGORY_PREFIX: Record<FandomTaxonomyCategoryKey, string> = {
			site: 's_',
			page: 'p_',
		};

		let rawValues = (this.fandomTaxonomy?.[category]?.[key] ?? []) as FandomTaxonomyValue[];

		// ensure rawVaules is an array
		if (!Array.isArray(rawValues)) {
			rawValues = [rawValues];
		}

		return (
			rawValues
				// filter out values that are not objects, or do not have a value string, or have an empty value string
				.filter(
					(value) =>
						typeof value === 'object' &&
						value &&
						typeof value?.value === 'string' &&
						value?.value.length > 0 &&
						typeof value?.confidence === 'number',
				)
				.map((value) => {
					return {
						...value,
						value: `${FANDOM_TAXONOMY_CATEGORY_PREFIX[category]}${value.value}`,
					};
				})
		);
	}

	private static getIasCategories() {
		const iasIds = [...(window?.fandomPageTagging?.iab_categories ?? [])];
		const safetyIds: string[] = [];
		const mappedIds: string[] = [];

		iasIds.forEach((id) => {
			if (IAS_SAFETY_IDS.includes(id)) {
				safetyIds.push(id);
			} else {
				mappedIds.push(id);
			}
		});

		return { iasSafetyIds: safetyIds, iasMappedIds: mappedIds };
	}

	// Sort the values by confidence
	private static orderByConfidence(values: FandomTaxonomyValue[]): FandomTaxonomyValue[] {
		return values.sort((a, b) => b.confidence - a.confidence);
	}

	// Add confidence category to the value
	private static addConfidenceToValue(value: FandomTaxonomyValue): FandomTaxonomyValue {
		return {
			...value,
			value: `${value.value}${normalizeConfidence(value.confidence)}`,
		};
	}

	// This function is used to get the value from the taxonomy value, and normalize it
	private static getNormalizedValue(value: FandomTaxonomyValue): string {
		return value.value.substring(0, MAX_VALUE_LENGTH);
	}
}
